import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Fade,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email,
  Phone,
  Lock,
  Login as LoginIcon,
  Public,
  ChatBubbleOutline,
  Forum,
  SupportAgent,
  HeadsetMic,
  Message
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { login } from "../store/slices/authSlice";

/* ===================== STYLES ===================== */

const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#F1F5F9",
  padding: theme.spacing(2),
}));

const LoginCard = styled(Paper)(({ theme }) => ({
  width: "100%",
  maxWidth: 380, // Reduced from 420
  padding: theme.spacing(3, 3), // Reduced vertical padding
  borderRadius: "20px", // Slightly tighter radius
  backgroundColor: "#FFFFFF",
  boxShadow: "0 10px 40px -10px rgba(0,0,0,0.08)",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5), // Reduced gap
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2.5),
    borderRadius: "16px",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#F8FAFC",
    borderRadius: "12px",
    transition: "all 0.2s",
    "& fieldset": {
      borderColor: "#E2E8F0",
    },
    "&:hover fieldset": {
      borderColor: "#CBD5E1",
    },
    "&.Mui-focused": {
      backgroundColor: "#FFFFFF",
      boxShadow: "0 0 0 3px rgba(2, 136, 209, 0.1)",
      "& fieldset": {
        borderColor: "#0288D1",
        borderWidth: "1.5px",
      },
    },
  },
  "& .MuiInputLabel-root": {
    color: "#64748B",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#0288D1",
    fontWeight: 600,
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: "12px",
  fontSize: "1rem",
  fontWeight: 600,
  textTransform: "none",
  color: "#fff",
  background: "linear-gradient(135deg, #29B6F6 0%, #0288D1 100%)",
  boxShadow: "0 4px 12px rgba(2, 136, 209, 0.3)",
  transition: "all 0.2s",
  marginTop: theme.spacing(1),
  "&:hover": {
    background: "linear-gradient(135deg, #039BE5 0%, #01579B 100%)",
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(2, 136, 209, 0.4)",
  },
  "&:active": {
    transform: "translateY(0)",
  },
  "&.Mui-disabled": {
    background: "#E2E8F0",
    color: "#94A3B8",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    background: "#E2E8F0",
    color: "#94A3B8",
    boxShadow: "none",
  },
}));

const BackgroundIcon = styled(Box)(({ theme, top, left, right, bottom, size, rotation, delay }) => ({
  position: "absolute",
  top: top,
  left: left,
  right: right,
  bottom: bottom,
  fontSize: size,
  color: "#0288D1",
  opacity: 0.04,
  transform: `rotate(${rotation || 0}deg)`,
  pointerEvents: "none",
  zIndex: 0,
  filter: "blur(1px)",
  animation: "float 10s ease-in-out infinite",
  animationDelay: delay || "0s",
  "@keyframes float": {
    "0%, 100%": { transform: `translateY(0) rotate(${rotation || 0}deg)` },
    "50%": { transform: `translateY(-20px) rotate(${rotation || 0}deg)` },
  },
}));

/* ===================== COMPONENT ===================== */

export default function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // 🔴 Redux state
  const { loading, error: authError } = useSelector((state) => state.auth);

  // 🟡 Local state
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");


  const { isAuthenticated } = useSelector((state) => state.auth);

  const [form, setForm] = useState({
    email: "",
    phone: "",
    password: "",
  });


  useEffect(() => {
    if (isAuthenticated) {
      navigate("/chat");
    }
  }, [isAuthenticated, navigate]);

  /* ===================== HANDLERS ===================== */

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.email || !form.password) {
      setError("Email and password are required");
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      setError("Invalid email address");
      return;
    }

    try {
      const result = await dispatch(
        login({
          email: form.email,
          password: form.password,
          phone: form.phone,

        })
      ).unwrap();

      console.log("Login API Response:", result); // Debug log

      // Check if temp password was used - must change password
      if (result.agent?.is_temp_password === true) {
        // Redirect to password change page (forced)
        navigate("/change-password", {
          state: {
            email: form.email,
            forceChange: true,
            customerId: result.agent.customer_id, // Pass customer_id
            agentId: result.agent.agent_id
          }
        });
      } else {
        // Normal login success - PASS customerId to chat route
        navigate("/chat", {
          state: {
            customerId: result.agent.customer_id, // This is what you need!
            agentId: result.agent.agent_id,
            agentName: result.agent.name
          }
        });
      }
    } catch (err) {
      setError(err?.message || "Login failed");
    }
  };

  /* ===================== UI ===================== */

  return (
    <PageWrapper>
      {/* Subtle Background Icons */}
      <BackgroundIcon top="10%" left="5%" size={120} rotation={-15} delay="0s">
        <ChatBubbleOutline fontSize="inherit" />
      </BackgroundIcon>

      <BackgroundIcon top="15%" right="10%" size={100} rotation={15} delay="2s">
        <SupportAgent fontSize="inherit" />
      </BackgroundIcon>

      <BackgroundIcon bottom="10%" left="10%" size={140} rotation={-10} delay="1s">
        <Forum fontSize="inherit" />
      </BackgroundIcon>

      <BackgroundIcon bottom="20%" right="5%" size={90} rotation={20} delay="3s">
        <HeadsetMic fontSize="inherit" />
      </BackgroundIcon>

      <BackgroundIcon top="40%" left="20%" size={60} rotation={45} delay="4s">
        <Message fontSize="inherit" />
      </BackgroundIcon>

      <Fade in timeout={600}>
        <LoginCard elevation={0} sx={{ position: "relative", zIndex: 1 }}>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box textAlign="center" mb={0.5}>
              <Box
                sx={{
                  width: 48, // Reduced from 56
                  height: 48,
                  bgcolor: "#E0F2FE",
                  color: "#0288D1",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                  mb: 1.5
                }}
              >
                <LoginIcon sx={{ fontSize: 26 }} />
              </Box>
              <Typography variant="h5" fontWeight={700} color="#1E293B" sx={{ mb: 0.5, letterSpacing: '-0.5px' }}>
                Welcome Back
              </Typography>
              <Typography variant="body2" color="#64748B" fontSize="0.875rem">
                Sign in to your dashboard
              </Typography>
            </Box>

            {(error || authError) && (
              <Alert severity="error" sx={{ borderRadius: "12px" }}>
                {error || authError?.message}
              </Alert>
            )}

            <StyledTextField
              fullWidth
              name="email"
              label="Email Address"
              value={form.email}
              onChange={handleChange}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: "#94A3B8" }} />
                  </InputAdornment>
                ),
              }}
            />

            <StyledTextField
              fullWidth
              name="phone"
              label="Phone Number"
              value={form.phone}
              onChange={handleChange}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone sx={{ color: "#94A3B8" }} />
                  </InputAdornment>
                ),
              }}
            />

            <StyledTextField
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={handleChange}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: "#94A3B8" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: "#94A3B8" }}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -1 }}>
              <Typography
                component="button"
                type="button"
                variant="body2"
                onClick={() => navigate("/forgot-password")}
                sx={{
                  color: "#0284C7",
                  textDecoration: "none",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  "&:hover": { color: "#0369A1" },
                  background: "none",
                  border: "none",
                  p: 0,
                }}
              >
                Forgot Password?
              </Typography>
            </Box>

            <GradientButton
              fullWidth
              type="submit"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? "Signing in..." : "Sign In"}
            </GradientButton>


          </Box>
        </LoginCard>
      </Fade>
    </PageWrapper>
  );
}
